import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CheckboxField,
  CustomTagsField,
  Divider,
  EmptyStation,
  type FormActionData,
  FormStation,
  InfoPanel,
  type InitialFormData,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  Section,
  SingleLineTextField,
  type StationMessage,
  formatDateTime,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import {
  IdentityProvider,
  type IdpConfiguration,
  useDeleteIdpConfigurationMutation,
  useIdpConfigurationsQuery,
  useUpdateIdpConfigurationMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import {
  type CustomBrandingConfig,
  CustomIdpBranding,
} from '../IdentityProviders/CustomIdpBranding/CustomIdpBranding';

interface FormData extends CustomBrandingConfig {
  id?: string;
  discoveryDocumentUrl?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  clientSecretIsSet?: boolean;
  scopes?: (string | null)[];
  logoutFromIdp?: boolean;
}

const getIdpConfigurationSchema =
  (): Yup.ObjectSchema<ObjectSchemaDefinition> => {
    return Yup.object<ObjectSchemaDefinition>({
      discoveryDocumentUrl: Yup.string()
        .nullable()
        .max(255)
        .test(
          'discovery-document-url',
          'Discovery Document URL must be a valid URL',
          validateUrl,
        )
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'Discovery Document URL is required when IDP is enabled. test',
          ),
          otherwise: Yup.string().nullable(),
        }),
      clientId: Yup.string()
        .max(255)
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'Client ID is required when IDP is enabled.',
          ),
          otherwise: Yup.string().nullable(),
        }),
      clientSecret: Yup.string()
        .when(['enabled', 'clientSecretIsSet'], {
          is: (enabled: boolean, clientSecretIsSet: boolean) =>
            enabled && !clientSecretIsSet,
          then: Yup.string().required(
            'Client Secret is required when IDP is enabled.',
          ),
          otherwise: Yup.string().nullable(),
        })
        .max(255),
      displayName: Yup.string()
        .max(200)
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'Display name is required when using a Custom IDP.',
          ),
          otherwise: Yup.string().nullable(),
        }),
      idpImageUrl: Yup.string()
        .max(1000)
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'IDP Image URL is required when using a Custom IDP.',
          ),
          otherwise: Yup.string().nullable(),
        }),
    });
  };

export const IdpCustomDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const { loading, data, error } = useIdpConfigurationsQuery({
    client: apolloClient,
    variables: {
      idpConfigurationCondition: {
        environmentId: envId,
        idpId: IdentityProvider.Custom,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [updateIdpConfig] = useUpdateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const idpConfiguration = data?.configurations?.nodes[0];
  let detailObj: FormData;
  if (idpConfiguration !== undefined) {
    detailObj = idpConfiguration;
    detailObj.discoveryDocumentUrl =
      idpConfiguration.discoveryDocumentUrl ?? '';
    detailObj.clientId = idpConfiguration.clientId ?? '';
  } else {
    detailObj = {
      discoveryDocumentUrl: null,
      clientId: null,
      clientSecret: null,
    };
  }

  const { actions } = useActions(envId, idpConfiguration?.id);

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: InitialFormData<FormData>,
    ): Promise<void> => {
      if (idpConfiguration !== undefined) {
        await updateIdpConfig({
          variables: {
            input: {
              id: initialData.data?.id,
              patch: createUpdateDto(formData, initialData.data),
            },
          },
        });
      }
    },
    [idpConfiguration, updateIdpConfig],
  );

  const [message] = useState<StationMessage>({
    title:
      'Certain Identity Providers may have different policies for automatically expiring Client Secrets. Please ensure if/when such expiration happens, the values stored here will be updated in a timely manner to provide an uninterrupted authentication experience.',
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Custom Identity Provider" subtitle="Configuration">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <FormStation<FormData>
          defaultTitle="Custom Identity Provider"
          subtitle="Configuration"
          actions={actions}
          validationSchema={getIdpConfigurationSchema()}
          initialData={{
            data: detailObj,
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={idpConfiguration ? <Panel /> : undefined}
          stationMessage={message}
        >
          <Form isNew={!idpConfiguration} />
        </FormStation>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpConfiguration>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{
  isNew: boolean;
}> = ({ isNew }) => {
  const { values } = useFormikContext<FormData>();

  return (
    <>
      {!isNew && (
        <>
          <Field
            name="enabled"
            label="IDP Status"
            options={[
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ]}
            transform={(value: string) => JSON.parse(value)}
            as={RadioField}
          />
          <Divider />
        </>
      )}

      <Field
        name="discoveryDocumentUrl"
        label="Discovery Document URL"
        autoComplete="off"
        tooltipContent={
          <div>
            The URL for the OpenID Connect Discovery Document provided by the ID
            provider. This can be found by referring the documentation of your
            ID provider. Some ID providers such as Okta refer to this as OpenID
            Configuration URL.
          </div>
        }
        as={SingleLineTextField}
      />
      <Field
        name="clientId"
        label="Client ID"
        autoComplete="off"
        as={SingleLineTextField}
      />
      <Field
        name="clientSecret"
        label="Client Secret"
        type="password"
        autoComplete="one-time-code"
        isSet={values?.clientSecretIsSet}
        as={SingleLineTextField}
      />

      <Field
        name="scopes"
        label="Scopes"
        tooltipContent={
          <div>
            Scopes to be included in OIDC authorization request. By default,
            OIDC scopes <em>openid</em>, <em>email</em> and <em>profile</em> are
            included. If any additional scopes are required, please refer to
            your ID provider documentation.
          </div>
        }
        as={CustomTagsField}
      />
      <Field
        name="logoutFromIdp"
        label="Auto Logout from Identity Provider"
        tooltipContent={
          <div>
            When enabled, logging out of the management system will also
            redirect the browser to the identity provider&apos;s logout
            endpoint, clearing the IDP session. The identity provider must
            support OpenID Connect RP-Initiated Logout, and the management
            system base URL (e.g.{' '}
            <em>https://your-management-system.eu.axinom.net</em>) must be
            registered as an allowed logout URL in the identity provider.
          </div>
        }
        as={CheckboxField}
      />
      <Divider />
      <CustomIdpBranding isEnabledByDefault={true} />
    </>
  );
};

const useActions = (
  envId: string,
  idpConfigId: string | undefined,
): {
  readonly actions: FormActionData[];
} => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [deleteIdpConfig] = useDeleteIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData[] = [];
  if (idpConfigId !== undefined) {
    actions.push({
      label: 'Delete Configuration',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await deleteIdpConfig({ variables: { input: { id: idpConfigId } } });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders`,
        );
      },
    });
  }

  return { actions } as const;
};

function createUpdateDto(
  currentValues: FormData,
  initialValues: FormData | null | undefined,
): Partial<FormData> {
  const updateData = getFormDiff(currentValues, initialValues);

  updateData.clientId = updateData.clientId === '' ? null : updateData.clientId;
  updateData.clientSecret =
    updateData.clientSecret === '' ? null : updateData.clientSecret;
  updateData.discoveryDocumentUrl =
    updateData.discoveryDocumentUrl === ''
      ? null
      : updateData.discoveryDocumentUrl;
  return updateData;
}

export const CustomProviderDetailCrumb: BreadcrumbResolver = () =>
  'Custom Identity Provider';
